import { Category, type GameV2 } from '@/api/games';
import type { IJackpotCampaignLevel } from '@/api/jackpot';
import { CurrencyCode } from '@/helpers/currency';

export const SLOTS_LINK = `/games/${Category.SLOTS}#gamesSection`;

export interface IHitRange {
  from: number;
  to: number;
}

export enum PoolLevelEnum {
  PIONEERS = 'pioneers',
  LEGENDS = 'legends',
  TITANS = 'titans',
}

export interface IPool {
  id: string;
  type: PoolLevelEnum;
  title: string;
  prize: number;
  minBet: number;
  hitRange: IHitRange;
}

export enum PoolStatusEnum {
  IN_HIT_ZONE = 'inHitZone',
  WIN_ANY_MOMENT = 'winAnyMoment',
  ABOUT_TO_BLOW = 'aboutToBlow',
}

export const LEVEL_BY_CODE = new Map<string, PoolLevelEnum>([
  ['jp_lvl_1', PoolLevelEnum.PIONEERS],
  ['jp_lvl2', PoolLevelEnum.LEGENDS],
  ['jp_lvl3', PoolLevelEnum.TITANS],
  ['pioneers', PoolLevelEnum.PIONEERS],
  ['legends', PoolLevelEnum.LEGENDS],
  ['titans', PoolLevelEnum.TITANS],
]);

export const POOL_TYPES = Object.values(PoolLevelEnum);

const POOL_STATUS_FRACTIONS: [PoolStatusEnum, number][] = [
  [PoolStatusEnum.ABOUT_TO_BLOW, 0.67],
  [PoolStatusEnum.WIN_ANY_MOMENT, 0.33],
  [PoolStatusEnum.IN_HIT_ZONE, 0],
];

export const JACKPOT_CURRENCY = CurrencyCode.EUR;

export const AVAILABLE_STAKES: Partial<Record<CurrencyCode, number[]>> = {
  [CurrencyCode.USD]: [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 3.5, 5, 7.5, 10, 12.5, 15,
    20, 25, 30, 40, 50, 75,
  ],
  [CurrencyCode.EUR]: [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 3.5, 4, 5, 7.5, 10, 12.5,
    15, 20, 25, 30, 40, 50, 75,
  ],
  [CurrencyCode.CAD]: [
    0.2, 0.25, 0.3, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10, 12.5, 20,
    25, 30, 35, 50, 75,
  ],
  [CurrencyCode.AUD]: [
    0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1, 1.4, 1.6, 2, 2.4, 2.8, 3, 3.2, 3.6, 4, 5,
    6, 7.5, 8, 10, 12, 15, 16, 20, 24, 25, 28, 30, 35, 40, 50, 60, 80,
  ],
  [CurrencyCode.NOK]: [
    1, 1.1, 2, 3, 5, 6, 7, 7.5, 8, 9, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55,
    75, 100, 110, 125, 150, 200, 250, 275, 300, 500, 600, 750,
  ],
  [CurrencyCode.NZD]: [
    0.2, 0.3, 0.5, 0.7, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 7.5, 8, 9, 10, 12.5, 15,
    20, 25, 30, 35, 40, 45, 50, 60, 75,
  ],
};

const SUPPORTED_CURRENCIES = new Set<string>(Object.keys(AVAILABLE_STAKES));

export const toSupportedCurrency = (currency: string | undefined) =>
  currency && SUPPORTED_CURRENCIES.has(currency)
    ? (currency as CurrencyCode)
    : CurrencyCode.USD;

export const findNearestStake = (amount: number, currency: CurrencyCode) =>
  AVAILABLE_STAKES[currency]?.find((stake) => stake >= amount) ?? amount;

const CENTS_DIVIDER = 100;

export const centsToAmount = (cents: number) => cents / CENTS_DIVIDER;

export const amountToCents = (amount: number) =>
  Math.round(amount * CENTS_DIVIDER);

export const convertAmount = (
  amount: number,
  rate: number,
  roundUp = false
): number => {
  // toFixed drops float noise such as 10.000000000000002, which would push ceil/floor a cent off.
  const cents = Number(((amount / rate) * CENTS_DIVIDER).toFixed(6));

  return (roundUp ? Math.ceil(cents) : Math.floor(cents)) / CENTS_DIVIDER;
};

// Codes differ between environments, so a level with an unknown code takes the first free tier in min bet order.
export const toPools = (levels: IJackpotCampaignLevel[]): IPool[] => {
  const sorted = [...levels].sort(
    (a, b) => a.minBetAmountCents - b.minBetAmountCents
  );
  const typeByCode = sorted.map(({ code }) => LEVEL_BY_CODE.get(code));
  const freeTypes = POOL_TYPES.filter((type) => !typeByCode.includes(type));

  return sorted.flatMap((level, index) => {
    const type = typeByCode[index] ?? freeTypes.shift();

    if (!type) return [];

    return {
      id: level.id,
      type,
      title: level.name,
      prize: centsToAmount(level.poolCents),
      minBet: centsToAmount(level.minBetAmountCents),
      hitRange: {
        from: centsToAmount(level.hitRange.fromCents),
        to: centsToAmount(level.hitRange.toCents),
      },
    };
  });
};

export const getPoolStatus = (
  prize: number,
  hitRange: IHitRange
): PoolStatusEnum | null => {
  const span = hitRange.to - hitRange.from;

  const reached = POOL_STATUS_FRACTIONS.find(
    ([, fraction]) => prize >= hitRange.from + fraction * span
  );

  return reached?.[0] ?? null;
};

const getHitRangeStep = (from: number) => {
  if (from < 200) return 5;
  if (from < 2000) return 10;
  return 100;
};

export const roundHitRange = (
  range: IHitRange,
  currency: CurrencyCode
): IHitRange => {
  if (currency === JACKPOT_CURRENCY) return range;

  const step = getHitRangeStep(range.from);

  return {
    from: Math.floor(range.from / step) * step,
    to: Math.ceil(range.to / step) * step,
  };
};

export type CentsById = Record<string, number>;

export const interpolateCents = (
  from: CentsById,
  to: CentsById,
  progress: number
): CentsById =>
  Object.fromEntries(
    Object.entries(to).map(([id, target]) => {
      const start = from[id] ?? target;

      return [
        id,
        target > start
          ? Math.floor(start + (target - start) * progress)
          : target,
      ];
    })
  );

export const pickFeaturedGames = (
  games: GameV2.Game[],
  identifiers: string[],
  currencies: string[]
): GameV2.Game[] => {
  const gameByIdentifier = new Map(
    games.map((game) => [game.identifier, game])
  );

  return identifiers
    .flatMap((identifier) => gameByIdentifier.get(identifier) ?? [])
    .filter(
      (game) =>
        !currencies.length ||
        currencies.some((currency) => currency in game.currencies)
    );
};
